import React from 'react';
import { useTheme } from '@mui/material';
import { DashboardCard, DashboardCardSkeleton } from '../../../../../shared/components';
import { DonutChart } from '../../../../../shared/components/charts';
import { AnnotatableChart } from '../../../../../shared/components/charts/AnnotatableChart';
import { WellnessScoreGauge } from './WellnessScoreGauge';
import { useMetricsTrends } from '../../../../../shared/hooks';
import { transformTrendsData } from '../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';
import type { DistributionEntry, StrainTrendMetric } from '../api/types';

interface StrainGaugeProps {
  isLoading: boolean;
  weatherFilters: MetricsFilters;
  showBreakdown?: boolean;
  breakdownOpen?: boolean;
  onToggleBreakdown?: () => void;
}

export const StrainGauge: React.FC<StrainGaugeProps> = ({
  isLoading,
  weatherFilters,
  showBreakdown,
  breakdownOpen,
  onToggleBreakdown,
}) => {
  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  return (
    <WellnessScoreGauge
      metric='strain'
      filterParams={weatherFilters}
      showBreakdown={showBreakdown}
      breakdownOpen={breakdownOpen}
      onToggleBreakdown={onToggleBreakdown}
    />
  );
};

interface StrainZonesProps {
  heartRateZones: DistributionEntry[] | undefined;
  isLoading: boolean;
}

export const StrainZones: React.FC<StrainZonesProps> = ({ heartRateZones, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  const zonesData = (heartRateZones ?? []).map((d) => ({
    label: d.name,
    value: d.value,
    color: d.name.includes('1-3') ? theme.palette.success.main : theme.palette.error.main,
  }));

  return (
    <DashboardCard
      title='Heart Rate Zones'
      subtitle='Distribution of time spent across heart rate intensity zones.'
      expandable
      expandedContent={<DonutChart data={zonesData} height={350} />}
    >
      <DonutChart data={zonesData} height={220} />
    </DashboardCard>
  );
};

interface StrainTrendProps {
  deptName: string;
  clientId: string | null;
  weatherFilters: MetricsFilters;
  selectedMetric: StrainTrendMetric;
}

const METRIC_CONFIG: Record<StrainTrendMetric, { label: string; color: string; yAxisMax?: number }> = {
  day_strain: { label: 'Day Strain', color: '#ff9800', yAxisMax: 21 },
  cardio_load: { label: 'Cardio Load', color: '#e91e63' },
  heart_rate: { label: 'Heart Rate', color: '#f44336', yAxisMax: 200 },
  calories_burn: { label: 'Calories Burned', color: '#9c27b0' },
};

export const StrainTrend: React.FC<StrainTrendProps> = ({ deptName, clientId, weatherFilters, selectedMetric }) => {
  const trendsQuery = useMetricsTrends({
    ...weatherFilters,
    metrics: ['day_strain', 'cardio_load', 'heart_rate', 'calories_burn'],
    granularity: 'daily',
  });

  const trendData = transformTrendsData(trendsQuery.data, [selectedMetric]);
  const config = METRIC_CONFIG[selectedMetric];

  return (
    <DashboardCard
      title='Avg Strain and Activity Trends'
      subtitle={`${deptName} ${config.label.toLowerCase()} over time`}
      expandable
      expandedContent={
        <AnnotatableChart
          clientId={clientId}
          startDate={weatherFilters.start_date}
          endDate={weatherFilters.end_date}
          weatherFilters={weatherFilters}
          data={trendData}
          xAxisKey='label'
          series={[{ dataKey: selectedMetric, label: config.label, color: config.color, area: true }]}
          yAxisMin={0}
          yAxisMax={config.yAxisMax}
          height={500}
        />
      }
    >
      <AnnotatableChart
        clientId={clientId}
        startDate={weatherFilters.start_date}
        endDate={weatherFilters.end_date}
        weatherFilters={weatherFilters}
        data={trendData}
        xAxisKey='label'
        series={[{ dataKey: selectedMetric, label: config.label, color: config.color, area: true }]}
        yAxisMin={0}
        yAxisMax={config.yAxisMax}
        height={250}
      />
    </DashboardCard>
  );
};
